'use client';
import React, { useEffect, useRef, useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface StatCard {
  stat: string;
  unit: string;
  label: string;
  sub: string;
  icon: string;
  intensity: 1 | 2 | 3;
}

const stats: StatCard[] = [
  {
    stat: '4.2',
    unit: 's',
    label: 'Repair order to sent invoice',
    sub: 'From the moment you tap "Complete RO" — parts, labor, taxes, markup — Invoice builds, formats, and sends the PDF before your tech walks back to the lift.',
    icon: 'BoltIcon',
    intensity: 1,
  },
  {
    stat: '$38K',
    unit: '/mo',
    label: 'Average monthly billing recovered',
    sub: 'Automatic parts-markup calculation catches every line item your current system leaves blank. Shops using Invoice recover an average $38,412/month in billing that was previously walking out the door.',
    icon: 'CurrencyDollarIcon',
    intensity: 2,
  },
  {
    stat: '97.4',
    unit: '%',
    label: 'Invoice accuracy rate',
    sub: 'Compared to 61% accuracy on handwritten tickets. Invoice pulls labor hours directly from MOTOR, applies your markup matrix, and cross-checks parts costs against your supplier feed — zero manual math.',
    icon: 'CheckCircleIcon',
    intensity: 3,
  },
  {
    stat: '2.1×',
    unit: '',
    label: 'Faster payment collection',
    sub: 'SMS and email invoices with embedded pay links get paid in 1.8 days on average versus 3.9 days for shops still mailing paper invoices. 85% of customers now prefer contactless payment.',
    icon: 'ArrowTrendingUpIcon',
    intensity: 3,
  },
];

const intensityStyles = {
  1: {
    border: 'border-[rgba(255,255,255,0.1)]',
    glow: '',
    statColor: 'text-white',
    iconBg: 'bg-[rgba(255,255,255,0.06)]',
    iconColor: 'text-[#C0C7D0]',
  },
  2: {
    border: 'border-[rgba(0,229,204,0.2)]',
    glow: 'shadow-[0_0_40px_rgba(0,229,204,0.08)]',
    statColor: 'teal-gradient-text',
    iconBg: 'bg-[rgba(0,229,204,0.1)]',
    iconColor: 'text-teal',
  },
  3: {
    border: 'border-[rgba(0,229,204,0.35)]',
    glow: 'shadow-[0_0_60px_rgba(0,229,204,0.15)]',
    statColor: 'teal-gradient-text',
    iconBg: 'bg-[rgba(0,229,204,0.15)]',
    iconColor: 'text-teal',
  },
};

const StatCardComponent: React.FC<{ card: StatCard; index: number }> = ({ card, index }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.2 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const s = intensityStyles[card.intensity];

  return (
    <div
      ref={ref}
      className={`glass-panel ${s.border} ${s.glow} p-8 md:p-10 transition-all duration-700 ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
      }`}
      style={{ transitionDelay: `${index * 120}ms` }}
    >
      {/* Icon */}
      <div className={`w-12 h-12 rounded-xl ${s.iconBg} flex items-center justify-center mb-6`}>
        <Icon name={card.icon as any} size={24} className={s.iconColor} />
      </div>

      {/* Stat — lands first */}
      <div className={`mono font-bold leading-none mb-1 ${s.statColor}`} style={{ fontSize: 'clamp(2.5rem, 6vw, 4rem)' }}>
        {card.stat}
        <span className="text-[1.5rem] ml-0.5 opacity-70">{card.unit}</span>
      </div>

      {/* Label */}
      <div className="heading font-semibold text-white text-lg mb-4">{card.label}</div>

      {/* Explanation fades in slightly after */}
      <p
        className={`text-[#C0C7D0] text-[14px] leading-relaxed transition-all duration-700 ${
          visible ? 'opacity-100' : 'opacity-0'
        }`}
        style={{ transitionDelay: `${index * 120 + 300}ms` }}
      >
        {card.sub}
      </p>
    </div>
  );
};

const StatsCascade: React.FC = () => {
  return (
    <section id="features" className="py-24 px-6">
      <div className="max-w-7xl mx-auto">
        {/* Section label */}
        <div className="reveal mb-14 text-center">
          <span className="text-[11px] tracking-[0.4em] uppercase text-[rgba(192,199,208,0.5)] font-medium">
            The Numbers First
          </span>
          <h2 className="heading font-black text-white mt-3" style={{ fontSize: 'clamp(2rem, 5vw, 3.5rem)' }}>
            Every scroll drops a hard number.
          </h2>
          <p className="text-[#C0C7D0] max-w-xl mx-auto mt-4 text-base leading-relaxed">
            No vague promises. No stock photography. Just the proof that shops running Invoice are billing more, faster, with fewer errors.
          </p>
        </div>

        {/* Bento grid — asymmetric */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-5">
          {/* Card 1 — small */}
          <div className="lg:col-span-5">
            <StatCardComponent card={stats[0]} index={0} />
          </div>
          {/* Card 2 — wide */}
          <div className="lg:col-span-7">
            <StatCardComponent card={stats[1]} index={1} />
          </div>
          {/* Card 3 — wide */}
          <div className="lg:col-span-7">
            <StatCardComponent card={stats[2]} index={2} />
          </div>
          {/* Card 4 — small */}
          <div className="lg:col-span-5">
            <StatCardComponent card={stats[3]} index={3} />
          </div>
        </div>
      </div>
    </section>
  );
};

export default StatsCascade;